import { Box, Button, Divider, Flex, Input, Text } from "@chakra-ui/react";
import { addDays, format, isToday, isTomorrow } from "date-fns";
import { LegacyRef, useRef } from "react";
import { FiCalendar } from "react-icons/fi";
import { IoImagesOutline, IoListOutline } from "react-icons/io5";

export interface DateSelectorWithViewProps {
  date: string;
  view: boolean;
  totalResults?: number;
  displayDate?: boolean;
  handleViewChange: (data: boolean) => void;
  handleDateChange: (data: string) => void;
}

function DateSelectorWithView({
  date,
  view,
  totalResults = 0,
  handleViewChange,
  handleDateChange,
  displayDate = true,
}: DateSelectorWithViewProps) {
  const now = new Date().toLocaleString();
  const next = addDays(new Date(), 1).toLocaleString();
  const ref = useRef<HTMLInputElement>();
  const Today = isToday(date);
  const Tomorrow = isTomorrow(date);
  const isActiveDate = !Today && !Tomorrow && !!date;

  return (
    <Box
      borderBottom={"2px solid #E5E5E5"}
      pb={"2"}
      fontSize={"14px"}
      mb={6}
      mt={4}
    >
      <Flex h="20px" alignItems={"center"} p={2}>
        <Text>Filters</Text>
        <Divider border="1px solid #E5E5E5" ml="10px" />
      </Flex>
      <Flex
        justifyContent={"space-between"}
        flexDirection={{ base: "column", md: "row" }}
        gap={{ base: 4, md: 0 }}
      >
        <Flex
          gap={2}
          p={2}
          alignItems={"center"}
          pointerEvents={displayDate ? "auto" : "none"}
          opacity={displayDate ? "100%" : "0%"}
          flexWrap="wrap"
        >
          <Text mr={2}>Date</Text>
          <Button
            size="sm"
            color="black"
            variant="outline"
            fontWeight={"normal"}
            border={Today ? "1px solid #007AC0" : "1px solid #979797"}
            backgroundColor={Today ? "primary.50" : "white"}
            disabled={true}
            sx={{
              rounded: "full",
            }}
            py={1}
            px={2}
            onClick={() => handleDateChange(now)}
          >
            Today
          </Button>
          <Flex
            border={Tomorrow ? "1px solid #007AC0" : "1px solid #979797"}
            backgroundColor={Tomorrow ? "primary.50" : "white"}
            sx={{
              rounded: "full",
            }}
            py={1}
            px={2}
            onClick={() => handleDateChange(next)}
            cursor="pointer"
          >
            Tomorrow
          </Flex>
          <Box position="relative">
            <Input
              type="date"
              ref={ref as LegacyRef<HTMLInputElement> | undefined}
              position="absolute"
              opacity={0}
              pointerEvents="none"
              min={format(new Date(), "yyyy-MM-dd")}
              onChange={(e) => {
                handleDateChange(e?.target?.value);
              }}
            />
            <Flex
              border={isActiveDate ? "1px solid #007AC0" : "1px solid #979797"}
              backgroundColor={isActiveDate ? "primary.50" : "white"}
              sx={{
                rounded: "full",
              }}
              py={1}
              px={4}
              alignItems={"center"}
              gap={2}
              cursor="pointer"
              onClick={() => ref.current?.showPicker()}
            >
              <FiCalendar />
              <Text>{isActiveDate ? date : "Choose Date"}</Text>
            </Flex>
          </Box>
        </Flex>
        <Flex
          gap={2}
          p={2}
          alignItems={"center"}
          justifyContent={{ base: "flex-start", md: "flex-end" }}
        >
          <Text mr={2}>View</Text>
          <Flex
            border={view ? "1px solid #007AC0" : "1px solid #979797"}
            backgroundColor={view ? "primary.50" : "white"}
            sx={{
              rounded: "full",
            }}
            py={1}
            px={4}
            gap={2}
            alignItems={"center"}
            onClick={() => handleViewChange(true)}
            cursor="pointer"
          >
            <IoImagesOutline /> <Text>Gallery</Text>
          </Flex>
          <Flex
            border={!view ? "1px solid #007AC0" : "1px solid #979797"}
            backgroundColor={!view ? "primary.50" : "white"}
            sx={{
              rounded: "full",
            }}
            py={1}
            px={4}
            gap={2}
            alignItems={"center"}
            onClick={() => handleViewChange(false)}
            cursor="pointer"
          >
            <IoListOutline />
            <Text>List</Text>
          </Flex>
        </Flex>
      </Flex>
      <Box h="20px">
        <Text ms={{ base: "10px", md: "50px" }} fontSize={"12px"}>
          {totalResults} results
        </Text>
      </Box>
    </Box>
  );
}

export default DateSelectorWithView;
